import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { CustomButton } from '../../utils/Button';
import { useMutation } from '@tanstack/react-query';
import { signUp } from '../../api/authAPI';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

function SignUpForm() {
  const navigate = useNavigate();
  const [showReq, setShowReq] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const password = watch('password', '');
  const email = watch('email', '');

  const { mutate: signUpUser, isPending: isLoading } = useMutation({
    mutationFn: signUp,
    onSuccess: () => {
      toast.success('Please verify your email.', {
        autoClose: 3000,
        className:
          'bg-[var(--yellow-primary)] text-black text-sm px-2 py-2 rounded-md',
        closeButton: false,
      });
      navigate(`/sign-up/verify-email/${encodeURIComponent(email)}`);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || 'Something went wrong';
      const errors = error?.response?.data?.errors;

      if (errors) {
        setFormErrors(errors);
      } else {
        toast.error(errorMessage, {
          autoClose: 3000,
          className:
            'bg-[var(--yellow-primary)] text-black text-sm px-2 py-2 rounded-md',
        });
      }
    },
  });

  //  Password criteria
  const passwordCriteria = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const isPasswordValid = Object.values(passwordCriteria).every(Boolean);

  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      phoneNumber: data.phoneNumber.replace(/^0/, '+234'),
    };
    console.log(formattedData);
    signUpUser(formattedData);
  };

  function handleInputFocus() {}

  return (
    <form className="my-3 font-duplet" onSubmit={handleSubmit(onSubmit)}>
      {/* EMAIL */}
      <div className="mb-3">
        <div className="relative">
          <input
            type="email"
            id="email"
            placeholder=" "
            className={`bg-white peer mt-1 block w-full px-3 pt-5 pb-2 border rounded-md shadow-sm text-gray-900  
            focus:outline-none focus:ring-2 focus:ring-black  
            ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'Invalid email address',
              },
            })}
          />
          <label
            htmlFor="email"
            className="absolute left-3 top-[2px] text-white text-sm transition-all duration-200 ease-in-out 
              peer-placeholder-shown:top-3 peer-placeholder-shown:text-black 
              peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-xs 
              peer-focus:text-black px-1"
          >
            Email address
          </label>
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
          )}
          {formErrors?.email?.[0] && (
            <p className="text-red-500 text-sm">{formErrors.email[0]}</p>
          )}
        </div>
      </div>

      {/* PHONE */}
      <div className="relative mb-3">
        <input
          type="text"
          id="phoneNumber"
          placeholder=" "
          className={`bg-white peer mt-1 block w-full px-3 pt-5 pb-2 border rounded-md shadow-sm text-gray-900 
            focus:outline-none focus:ring-2 focus:ring-black  
            ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'}`}
          {...register('phoneNumber', {
            required: 'Phone number is required',
            pattern: {
              value: /^[0-9]+$/,
              message: 'Invalid phone number',
            },
          })}
        />
        <label
          htmlFor="phoneNumber"
          className="absolute left-3 top-[2px] text-white text-sm transition-all duration-200 ease-in-out 
            peer-placeholder-shown:top-3 peer-placeholder-shown:text-black 
            peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-xs 
            peer-focus:text-black px-1"
        >
          Phone number
        </label>
        {errors.phoneNumber && (
          <p className="mt-1 text-sm text-red-500">
            {errors.phoneNumber.message}
          </p>
        )}
        {formErrors?.phoneNumber?.[0] && (
          <p className="text-red-500 text-sm">{formErrors.phoneNumber[0]}</p>
        )}
      </div>

      {/* PASSWORD */}
      <div className="relative mb-3">
        <input
          type={showPassword ? 'text' : 'password'}
          id="password"
          placeholder=" "
          className={`peer mt-1 block w-full px-3 pt-5 pb-2 border rounded-md shadow-sm text-gray-900 
            bg-white focus:outline-none focus:ring-2 focus:ring-black
            ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 8,
              message:
                'Password must be at least 8 characters long and include uppercase, lowercase letters, and a number.',
            },
          })}
          onFocus={() => setShowReq(true)}
          onBlur={() => setShowReq(false)}
        />
        <label
          htmlFor="password"
          className="absolute left-3 top-[2px] text-white text-sm transition-all duration-200 ease-in-out
            peer-placeholder-shown:top-3 peer-placeholder-shown:text-gray-600
            peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-xs
            peer-focus:text-black  px-1"
        >
          Password
        </label>
        <button
          type="button"
          aria-label="Toggle password visibility"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-3 flex items-center justify-center px-2 rounded focus:outline-none focus:ring-2 focus:ring-transparent h-[50px]"
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
        {errors.password && (
          <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
        )}
      </div>

      {/*  Password Criteria Feedback */}
      {password.length > 0 && showReq && (
        <ul className="text-sm text-gray-600 mb-4 space-y-1 transition-all duration-300">
          <li
            className={
              passwordCriteria.minLength ? 'text-green-600' : 'text-red-500'
            }
          >
            • At least 8 characters
          </li>
          <li
            className={
              passwordCriteria.hasUppercase ? 'text-green-600' : 'text-red-500'
            }
          >
            • At least one uppercase letter
          </li>
          <li
            className={
              passwordCriteria.hasLowercase ? 'text-green-600' : 'text-red-500'
            }
          >
            • At least one lowercase letter
          </li>
          <li
            className={
              passwordCriteria.hasNumber ? 'text-green-600' : 'text-red-500'
            }
          >
            • At least one number
          </li>
          <li
            className={
              passwordCriteria.hasSpecialChar
                ? 'text-green-600'
                : 'text-red-500'
            }
          >
            • At least one special character
          </li>
        </ul>
      )}

      {/* BUTTON */}
      <CustomButton
        type="submit"
        text={
          isLoading ? (
            <div className="flex items-center justify-center">
              <span className="font-duplet mr-1">Creating account...</span>
              <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin inline-block" />
            </div>
          ) : (
            'Create account'
          )
        }
        bgColor="var(--yellow-primary)"
        hoverColor="var(--btn-hover-bg-primary)"
        width="100%"
        textSize="1.125rem"
        fontWeight="700"
        borderRadius="40px"
        disabled={isLoading || !isPasswordValid}
        className="mt-4 py-3 px-6 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50"
      />
    </form>
  );
}

export default SignUpForm;
